import { useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatDollar } from "../utils/format";

export interface AvgPricePoint {
  time: string;
  end: string;
  value: number;
}

interface AvgPriceChartProps {
  data?: AvgPricePoint[];
  duration: string;
  loading?: boolean;
}

const COLOR = "#60A5FA";
const HEIGHT = 260;
const PAD = { top: 16, right: 12, bottom: 30, left: 12 };

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const frac = max / pow;
  let nice = 10;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 2.5) nice = 2.5;
  else if (frac <= 5) nice = 5;
  return nice * pow;
}

function formatXLabel(iso: string, duration: string): string {
  const d = new Date(iso);
  if (duration === "1y")
    return d.toLocaleString("fa-IR", { month: "short" });
  if (duration === "1w")
    return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  if (duration === "1mo") return d.toLocaleString("fa-IR", { day: "numeric" });
  return d.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
}

function formatRange(start: string, end: string, duration: string): string {
  const s = new Date(start);
  const e = new Date(end);
  if (duration === "1y")
    return s.toLocaleString("fa-IR", { month: "long", year: "numeric" });
  if (duration === "1w" || duration === "1mo")
    return `${s.toLocaleString("fa-IR", { day: "numeric", month: "long" })} — ${e.toLocaleString("fa-IR", { day: "numeric", month: "long" })}`;
  return `${s.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" })} — ${e.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" })}`;
}

interface Pt {
  x: number;
  y: number;
}

// Smooths a polyline through the given points using Catmull-Rom -> cubic
// Bezier conversion (same technique as the dashboard stacked area chart).
function smoothSegments(pts: Pt[], minY: number, maxY: number): string {
  if (pts.length === 0) return "";
  let d = "";
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    let c1x = p1.x + (p2.x - p0.x) / 6;
    let c1y = p1.y + (p2.y - p0.y) / 6;
    let c2x = p2.x - (p3.x - p1.x) / 6;
    let c2y = p2.y - (p3.y - p1.y) / 6;
    c1y = Math.min(maxY, Math.max(minY, c1y));
    c2y = Math.min(maxY, Math.max(minY, c2y));
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

export default function AvgPriceChart({ data, duration, loading }: AvgPriceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hoveredBucket, setHoveredBucket] = useState<number | null>(null);
  const [mouseX, setMouseX] = useState<number | null>(null);
  const [mouseY, setMouseY] = useState<number | null>(null);
  const [tooltipW, setTooltipW] = useState(0);
  const [tooltipH, setTooltipH] = useState(0);
  const uid = useId().replace(/[:]/g, "");

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, data]);

  // Measure the tooltip whenever the hovered bucket changes so it stays inside
  // the plot area. Placed with the other hooks (before any early return) to
  // keep the hook order stable across loading states.
  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipW(tooltipRef.current.offsetWidth);
      setTooltipH(tooltipRef.current.offsetHeight);
    }
  }, [hoveredBucket]);

  const points = useMemo(
    () => (data || []).map((p) => ({ time: p.time, end: p.end, value: Number(p.value) || 0 })),
    [data]
  );

  const maxY = useMemo(
    () => niceMax(Math.max(0, ...points.map((p) => p.value))),
    [points]
  );

  const n = points.length;
  const innerW = Math.max(1, width - PAD.left - PAD.right);
  const innerH = HEIGHT - PAD.top - PAD.bottom;
  const x = (i: number) => (n <= 1 ? PAD.left + innerW / 2 : PAD.left + (i / (n - 1)) * innerW);
  const y = (v: number) => PAD.top + innerH - (maxY > 0 ? v / maxY : 0) * innerH;

  if (loading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <div className="h-44 w-full bg-surface-hover rounded animate-pulse" />
      </div>
    );
  }

  if (!n) {
    return (
      <div className="h-[260px] flex items-center justify-center text-text-tertiary">
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const ticks = [0, 1, 2, 3, 4].map((i) => (maxY * i) / 4);

  const topPts = points.map((p, i) => ({ x: x(i), y: y(p.value) }));
  const baseline = PAD.top + innerH;
  const topStart = topPts[0];
  const bottomStart = topPts[topPts.length - 1];
  const linePath = `M ${topStart.x.toFixed(1)} ${topStart.y.toFixed(1)}${smoothSegments(topPts, PAD.top, baseline)}`;
  const areaPath = `${linePath} L ${bottomStart.x.toFixed(1)} ${baseline.toFixed(1)} L ${topStart.x.toFixed(1)} ${baseline.toFixed(1)} Z`;

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;
    const ratio = (relX - PAD.left) / innerW;
    const index = Math.min(Math.max(Math.round(ratio * (n - 1)), 0), n - 1);
    setHoveredBucket(index);
    setMouseX(relX);
    setMouseY(relY);
  };

  const isEmpty = !n || maxY <= 0;

  const GAP = 10;
  const TOOLTIP_EDGE = 6;
  const rawTop = mouseY != null ? mouseY - tooltipH / 2 : PAD.top + 2;
  const tooltipTop = Math.min(Math.max(rawTop, PAD.top + 2), HEIGHT - tooltipH - TOOLTIP_EDGE);
  let tooltipLeft = PAD.left;
  if (mouseX != null) {
    const spaceLeft = mouseX - GAP;
    const spaceRight = width - mouseX - GAP;
    if (spaceRight >= spaceLeft) {
      tooltipLeft = mouseX + GAP;
    } else {
      tooltipLeft = mouseX - GAP - tooltipW;
    }
    tooltipLeft = Math.min(Math.max(tooltipLeft, TOOLTIP_EDGE), width - tooltipW - TOOLTIP_EDGE);
  }
  const tooltipLeftPct = `${tooltipLeft}px`;

  return (
    <div>
      <div
        ref={containerRef}
        dir="ltr"
        className="relative select-none"
        style={{ height: HEIGHT }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredBucket(null)}
      >
        {isEmpty ? (
          <div className="h-full w-full flex items-center justify-center text-text-tertiary">
            <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
          </div>
        ) : (
          <>
            {/* Y axis ticks */}
            <div className="absolute left-0 top-0 bottom-0" style={{ width: PAD.left }}>
              {ticks.map((t) => (
                <span
                  key={t}
                  dir="rtl"
                  className="absolute right-0 -translate-y-1/2 text-[10px] text-text-tertiary"
                  style={{ top: `${(y(t) / HEIGHT) * 100}%` }}
                >
                  {formatDollar(t)}
                </span>
              ))}
            </div>

            {width > 0 && (
              <svg width={width} height={HEIGHT} className="block overflow-visible">
                <defs>
                  <linearGradient id={`${uid}-area`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={COLOR} stopOpacity="0.35" />
                    <stop offset="100%" stopColor={COLOR} stopOpacity="0" />
                  </linearGradient>
                </defs>

                {ticks.map((t) => (
                  <line
                    key={t}
                    x1={PAD.left}
                    x2={width - PAD.right}
                    y1={y(t)}
                    y2={y(t)}
                    stroke="rgb(var(--color-border))"
                    strokeWidth="1"
                  />
                ))}

                <path d={areaPath} fill={`url(#${uid}-area)`} />
                <path
                  d={linePath}
                  fill="none"
                  stroke={COLOR}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {hoveredBucket != null && (
                  <>
                    <line
                      x1={x(hoveredBucket)}
                      x2={x(hoveredBucket)}
                      y1={PAD.top}
                      y2={PAD.top + innerH}
                      stroke="rgb(var(--text-main))"
                      strokeWidth="1"
                      strokeDasharray="3 3"
                      opacity="0.35"
                    />
                    <circle
                      cx={x(hoveredBucket)}
                      cy={y(points[hoveredBucket].value)}
                      r="4"
                      fill={COLOR}
                      stroke="#fff"
                      strokeWidth="1.5"
                    />
                  </>
                )}
              </svg>
            )}

            {/* Tooltip */}
            {hoveredBucket != null && (
              <div
                dir="rtl"
                className="absolute z-30 pointer-events-none"
                style={{ left: tooltipLeftPct, top: tooltipTop }}
              >
                <div
                  ref={tooltipRef}
                  className="px-3 py-2.5 rounded-btn whitespace-nowrap"
                  style={{
                    background: "rgba(26,26,26,0.92)",
                    color: "#fff",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  }}
                >
                  <p className="text-[11px] mb-1.5" style={{ opacity: 0.8 }}>
                    {formatRange(points[hoveredBucket].time, points[hoveredBucket].end, duration)}
                  </p>
                  <div className="flex items-center justify-between gap-5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: COLOR }} />
                      <span className="text-[11px]">میانگین قیمت</span>
                    </div>
                    <span className="text-xs font-semibold" style={{ color: COLOR }}>
                      ${formatDollar(points[hoveredBucket].value)} / 1M
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {!isEmpty && (
        <div className="flex gap-1 mt-1" dir="ltr">
          {points.map((p, i) => (
            <div
              key={i}
              dir="rtl"
              className="flex-1 text-center text-[10px] text-text-tertiary truncate"
            >
              {formatXLabel(p.time, duration)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}